import { Injectable } from "@angular/core";

import { AppAction, AppError, ChatFeedback, IChatItem, isAppError } from "../types";
import {
  convertFilesToDataUris,
  createAppError,
  createAttachmentMetadata,
  FileConversionResult,
  getErrorCodeFromMessage,
  getErrorCodeFromResponse,
  isTokenExpiredError,
  parseErrorFromResponse,
  retryWithBackoff,
} from "../utils";
import { ChatAgentService } from "./chat-agent.service";
import { parseSseLine, splitSseBuffer } from "./parsers";

@Injectable({ providedIn: "root" })
export class ChatService {

  private currentStreamAbort?: AbortController;
  // Flag indicating an intentional user cancellation of the active stream.
  private streamCancelled = false;

  /**
   * @param _agentService Agente contra el que habla el chat
   */
  constructor(private _agentService: ChatAgentService) {}

  /**
   * Prepare message payload with optional file attachments.
   * Converts files to data URIs and separates images from documents.
   *
   * @param text - Message text content
   * @param files - Optional array of files (images and documents)
   * @returns Payload with content, image URIs, file attachments, and attachment metadata
   */
  private async prepareMessagePayload(
    text: string,
    files?: FileConversionResult[],
  ): Promise<{
    content: string;
    imageDataUris: string[];
    fileDataUris: Array<{
      dataUri: string;
      fileName: string;
      mimeType: string;
    }>;
    attachments: IChatItem["attachments"];
  }> {
    let imageDataUris: string[] = [];
    let fileDataUris: Array<{
      dataUri: string;
      fileName: string;
      mimeType: string;
    }> = [];
    let attachments: IChatItem["attachments"] = undefined;

    if (files && files.length > 0) {
      const imageResults = files.filter(f =>
      f.mimeType.startsWith("image/")
    );

    const fileResults = files.filter(f =>
      !f.mimeType.startsWith("image/")
    );

    imageDataUris = imageResults.map(f => f.dataUri);

    fileDataUris = fileResults.map(f => ({
      dataUri: f.dataUri,
      fileName: f.name,
      mimeType: f.mimeType,
    }));

    attachments = createAttachmentMetadata(files);
  }

  return { content: text, imageDataUris, fileDataUris, attachments };
  }

  /**
   * Construct request body for chat API.
   *
   * @param message - User message text
   * @param conversationId - Current conversation ID (null for new conversations)
   * @param imageDataUris - Array of base64 data URIs for images
   * @param fileDataUris - Array of file attachments with metadata
   * @returns Request body object
   */
  private constructRequestBody(
    message: string,
    conversationId: string | null,
    session: string | null,
    imageDataUris: string[],
    fileDataUris: Array<{
      dataUri: string;
      fileName: string;
      mimeType: string;
    }>,
  ): Record<string, any> {
    return {
      // El backend requiere "message" como string no-nulo; si viene undefined,
      // JSON.stringify lo elimina del body y la deserialización falla con 400
      // antes de que el código del controller llegue a ejecutarse.
      message: message ?? "",
      conversationId,
      session,
      imageDataUris: imageDataUris.length > 0 ? imageDataUris : undefined,
      fileDataUris: fileDataUris.length > 0 ? fileDataUris : undefined,
    };
  }

  /**
   * Initiate streaming fetch request to chat API.
   * Validates response and throws typed errors on failure.
   *
   * @param url - API endpoint URL
   * @param token - Access token
   * @param body - Request body
   * @param signal - Abort signal for cancellation
   * @returns Response object
   * @throws {AppError} If request fails or response is not OK
   */
  private async initiateStream(
    url: string,
    token: string,
    body: Record<string, any>,
    signal: AbortSignal,
  ): Promise<Response> {
    //console.log("Initiating stream with body:", JSON.stringify(body));
    console.log("To URL:", url);
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok) {
      const errorMessage = await parseErrorFromResponse(res);
      const errorCode = getErrorCodeFromResponse(res);
      throw createAppError(new Error(errorMessage), errorCode);
    }

    return res;
  }

  /**
   * Send a message and stream the response from the Azure AI Agent.
   * Orchestrates authentication, file conversion, optimistic UI updates, and streaming.
   *
   * @param messageText - The user's message text
   * @param currentConversationId - Current conversation ID (null for new conversations)
   * @param files - Optional array of files to attach (images and documents)
   * @throws {Error} If authentication fails or API request fails
   *
   * @remarks
   * Token acquisition: Attempts acquireTokenSilent first, falls back to acquireTokenPopup.
   * Retries failed requests up to 3 times with exponential backoff.
   */
  async *sendMessage(
    token: string,
    messageText: string,
    currentConversationId: string | null,
    session: string | null,
    files?: FileConversionResult[],
  ): AsyncGenerator<AppAction, void, unknown> {
    if (this.currentStreamAbort) {
      this.streamCancelled = true;
      this.currentStreamAbort.abort();
      yield { type: "CHAT_CANCEL_STREAM" };
    }

    try {
      const { content, imageDataUris, fileDataUris, attachments } =
        await this.prepareMessagePayload(messageText, files);

      const userMessage: IChatItem = {
        id: Date.now().toString(),
        role: "user",
        content,
        attachments,
        more: {
          time: new Date().toISOString(),
        },
      };

      yield { type: "CHAT_SEND_MESSAGE", message: userMessage };

      const assistantMessageId = (Date.now() + 1).toString();
      yield { type: "CHAT_ADD_ASSISTANT_MESSAGE", messageId: assistantMessageId };
      yield { type: "CHAT_START_STREAM", conversationId: currentConversationId || undefined, messageId: assistantMessageId };

      if (this.currentStreamAbort) {
        this.streamCancelled = true;
        this.currentStreamAbort.abort();
        yield { type: "CHAT_CANCEL_STREAM" };
        // Dale un tick para que processStream observe la cancelación
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      this.streamCancelled = false; // reset ANTES de crear el nuevo controller
      this.currentStreamAbort = new AbortController();

      const requestBody = this.constructRequestBody(
        messageText,
        currentConversationId,
        session,
        imageDataUris,
        fileDataUris,
      );

      const response = await retryWithBackoff(
        async () =>
        {
           // Nuevo controller por cada intento
          this.currentStreamAbort = new AbortController();
          return this.initiateStream(
            `${this._agentService.url}/chat/stream`,
            token,
            requestBody,
            this.currentStreamAbort!.signal,
          )
        },
        3,
        1000,
      );

      // Process the SSE stream and yield actions for each event
      // This will yield actions for chunks, annotations, reasoning, usage, and completion events
      for await (const action of this.processStream(response,assistantMessageId, currentConversationId)) {
        yield action;
      }

      this.currentStreamAbort = undefined;
      this.streamCancelled = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      if (isTokenExpiredError(error)) {
        yield { type: "AUTH_TOKEN_EXPIRED" };
      }

      const appError: AppError = isAppError(error)
        ? error
        : createAppError(error, getErrorCodeFromMessage(error), () =>
            this.sendMessage(token, messageText, currentConversationId, session, files),
          );

      yield { type: "CHAT_ERROR", error: appError };
      throw error;
    }
  }

  /**
   * Process Server-Sent Events stream from the API.
   * Implements duplicate chunk suppression to prevent UI flicker.
   *
   * @param response - Fetch Response object with SSE stream
   * @param messageId - ID of the assistant message being streamed
   * @param currentConversationId - Current conversation ID (null for new conversations)
   * @throws {Error} If stream is not readable or parsing fails
   */
  private async *processStream(
    response: Response,
    messageId: string,
    currentConversationId: string | null,
  ): AsyncGenerator<AppAction, void, unknown> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      const error = createAppError(
        new Error(`Response body is not readable for message ${messageId}`),
        "STREAM",
      );
      yield { type: "CHAT_ERROR", error };
      throw error;
    }

    const CHUNK_TIMEOUT_MS = 300_000; // 5m sin recibir nada = timeout
    let newConversationId = currentConversationId;
    let lastChunkContent: string | undefined;
    let buffer = "";

    // Helper: race entre reader.read() y un timeout
    const readWithTimeout = (timeoutMs: number) => {
      return Promise.race([
        reader.read(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("CHUNK_TIMEOUT")), timeoutMs)
        ),
      ]);
    };

    try {
      while (true) {
        if (this.streamCancelled) {
          break;
        }

        // Usa readWithTimeout en lugar de reader.read() directamente
        const { done, value } = await readWithTimeout(CHUNK_TIMEOUT_MS);
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const [lines, remaining] = splitSseBuffer(buffer);
        buffer = remaining;

        for (const line of lines) {
          const event = parseSseLine(line);
          if (!event) continue;

          if (event.data?.error) {
            console.error(
              "[ChatService] SSE error event received:",
              event.data.error,
            );
            const error = createAppError(
              new Error(
                event.data.error.message ||
                  event.data.error ||
                  "Stream error occurred",
              ),
              "STREAM",
            );
            yield { type: "CHAT_ERROR", error };
            throw error;
          }

          switch (event.type) {
            case "conversationId":
              if (!newConversationId) {
                newConversationId = event.data.conversationId;
                yield {
                  type: "CHAT_START_STREAM",
                  conversationId: event.data.conversationId,
                  messageId,
                };
              }
              break;

            case "chunk":
              if (event.data.content !== lastChunkContent) {
                yield{
                  type: "CHAT_STREAM_CHUNK",
                  messageId,
                  content: event.data.content,
                };
                lastChunkContent = event.data.content;
              }
              break;

            case "annotations":
              if (event.data.annotations && event.data.annotations.length > 0) {
                yield {
                  type: "CHAT_STREAM_ANNOTATIONS",
                  messageId,
                  annotations: event.data.annotations,
                };
              }
              break;

            case "mcpApprovalRequest":
              if (event.data.approvalRequest) {
                yield {
                  type: "CHAT_MCP_APPROVAL_REQUEST",
                  messageId,
                  approvalRequest: event.data.approvalRequest,
                  previousResponseId: newConversationId,
                };
              }
              break;

            case "reasoning":
              yield {
                type: "CHAT_STREAM_REASONING",
                messageId,
                content: event.data.content,
                tool: event.data.tool,
                callToolId: event.data.callToolId,
                status: event.data.status,
              };
              break;

            case "usage":
              yield {
                type: "CHAT_STREAM_COMPLETE",
                usage: {
                  promptTokens: event.data.promptTokens,
                  completionTokens: event.data.completionTokens,
                  totalTokens: event.data.totalTokens,
                  duration: event.data.duration,
                },
              } ;
              break;
            case "session":
              yield {
                type: "CHAT_SESSION",
                session: event.data.content,
              };
              break;

            case "chartBlueprint":
              yield {
                type: "CHAT_STREAM_CHART_BLUEPRINT",
                messageId,
                cardJson: event.data.content,
              };
              break;

            case "done":
              yield {
                type: "CHAT_DONE",
              };
              return;

            case "error":
              const error = createAppError(
                new Error(
                  `Stream error for message ${messageId}: ${event.data.message}`,
                ),
                "STREAM",
              );
              yield { type: "CHAT_ERROR", error };
              throw error;
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
          // Cancelación intencional O signal ya abortado en reintento — no es error
          return;
      }

      const appError =
        error instanceof Error && "code" in error
          ? error
          : createAppError(
              new Error(
                `Stream processing failed: ${error instanceof Error ? error.message : String(error)} (Conversation: ${currentConversationId}, Message: ${messageId})`,
              ),
              "STREAM",
            );
      yield { type: "CHAT_ERROR", error: appError as AppError };
      throw error;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Reader may already be released
      }
    }
  }

  /**
   * Send approval response for an MCP tool call.
   *
   * @param approvalRequestId - ID of the approval request
   * @param approved - Whether the tool call was approved
   * @param previousResponseId - Response ID to continue from
   * @param conversationId - Current conversation ID
   */
  async *sendMcpApproval(
    token: string,
    approvalRequestId: string,
    approved: boolean,
    previousResponseId: string,
    conversationId: string,
  ): AsyncGenerator<AppAction, void, unknown> {
    try {
      const assistantMessageId = Date.now().toString();
      yield {
        type: "CHAT_ADD_ASSISTANT_MESSAGE",
        messageId: assistantMessageId,
      };
      yield {
        type: "CHAT_START_STREAM",
        conversationId,
        messageId: assistantMessageId,
      };

      if (this.currentStreamAbort) {
        this.streamCancelled = true;
        this.currentStreamAbort.abort();
        yield { type: "CHAT_CANCEL_STREAM" };
        // Dale un tick para que processStream observe la cancelación
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      this.streamCancelled = false; // reset ANTES de crear el nuevo controller
      this.currentStreamAbort = new AbortController();

      const requestBody = {
        message: approved ? "Approved" : "Rejected",
        conversationId,
        previousResponseId,
        mcpApproval: {
          approvalRequestId,
          approved,
        },
      };

      const response = await retryWithBackoff(
        async () =>
        {
           // Nuevo controller por cada intento
          this.currentStreamAbort = new AbortController();
          return this.initiateStream(
            `${this._agentService.url}/chat/stream`,
            token,
            requestBody,
            this.currentStreamAbort!.signal,
          )
        },
        3,
        1000,
      );

      for await (const action of this.processStream(response, assistantMessageId, conversationId)) {
        yield action;
      }
      this.currentStreamAbort = undefined;
      this.streamCancelled = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      const appError: AppError = isAppError(error)
        ? error
        : createAppError(error, getErrorCodeFromMessage(error));

      yield { type: "CHAT_ERROR", error: appError };
      throw error;
    }
  }

  /**
   * Función que sirve para eliminar una conversación específica.
   * @param conversationId Indica el id de la conversación
   * @param token Indica el token para realizar la petición
   */
  public async deleteConversation(
    conversationId: string,
    token: string,
  ): Promise<void> {
    const res = await fetch(`${this._agentService.url}/chat/conversation/${conversationId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }
    });

    if (!res.ok) {
      const errorMessage = await parseErrorFromResponse(res);
      const errorCode = getErrorCodeFromResponse(res);
      throw createAppError(new Error(errorMessage), errorCode);
    }
  }

  /**
   * Save user feedback for a chat message.
   *
   * @param feedback - Feedback payload
   * @param token - Access token
   */
  public async saveChatFeedback(feedback: ChatFeedback, token: string): Promise<void> {
    const response = await fetch(`${this._agentService.url}/chat/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(feedback),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Error saving feedback: ${response.status} - ${text}`);
    }
  }

  /**
   * Clear chat history and reset to empty state.
   * Dispatches CHAT_CLEAR action to remove all messages and conversation ID.
   */
  clearChat(): AppAction {
    return { type: "CHAT_CLEAR" };
  }

  /**
   * Clear current error state without affecting chat history.
   * Dispatches CHAT_CLEAR_ERROR action.
   */
  clearError(): AppAction {
    return { type: "CHAT_CLEAR_ERROR" };
  }

  /**
   * Cancel the current streaming response if any is active.
   * Abort controller is not cleared immediately to allow processStream
   * to observe the cancellation flag and exit gracefully.
   */
  cancelStream(): AppAction | void {
    if (this.currentStreamAbort) {
      this.streamCancelled = true;
      this.currentStreamAbort.abort();
      return { type: "CHAT_CANCEL_STREAM" };
    }
  }
}
